import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LuMail, LuKey, LuArrowLeft, LuShield } from "react-icons/lu";
import { api, ApiError } from "@/lib/api";

/**
 * Magic-code login. Two-step:
 *   1. Operator enters their email -> /auth/login
 *   2. Operator enters the 6-digit code -> /auth/verify -> session JWT
 *
 * In dev (`CONSOLE_DEV_LOG_CODES=1` on the console worker) the code is
 * printed to the worker stdout instead of being emailed. The login API
 * surfaces a `devHint` flag so we can show a short note to that effect.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devHint, setDevHint] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.auth.login(email.trim());
      setDevHint(res.devHint ?? null);
      setStep("code");
      toast.success("If that email is allowed, a code has been sent.");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to request a code.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.auth.verify(email.trim(), code.trim());
      // Drop any cached "auth/me 401" so the guard re-fetches and lets us in.
      await qc.invalidateQueries({ queryKey: ["console", "auth", "me"] });
      toast.success("Signed in.");
      navigate("/overview", { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Invalid code.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[var(--color-bg)] p-6 overflow-hidden">
      <div
        className="absolute inset-0 opacity-60 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 0%, rgba(139, 92, 246, 0.15), transparent 70%), radial-gradient(40% 40% at 100% 100%, rgba(65, 209, 255, 0.08), transparent 60%)",
        }}
      />

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <img
            src="/logo.png"
            alt="destroy.network"
            className="w-12 h-12 rounded-lg ring-1 ring-[var(--color-border)] object-cover mb-3"
          />
          <p className="text-sm font-semibold text-[var(--color-text)]">
            destroy<span className="text-[var(--color-accent-light)]">.</span>network
          </p>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-subtle)] mt-1">
            Operator Console
          </p>
        </div>

        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-7 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.6)]">
          <div className="flex items-center gap-2 mb-1">
            <LuShield className="w-4 h-4 text-[var(--color-accent-light)]" />
            <h1 className="text-base font-semibold text-[var(--color-text)]">
              Sign in
            </h1>
          </div>
          <p className="text-sm text-[var(--color-text-muted)] mb-6">
            {step === "email"
              ? "Enter your operator email. We'll send a 6-digit code."
              : `Code sent to ${email}.`}
          </p>

          {step === "email" ? (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)] mb-2">
                  Email
                </label>
                <div className="relative">
                  <LuMail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-subtle)] pointer-events-none" />
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:border-[var(--color-accent)] transition-colors"
                    placeholder="you@example.com"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting || !email}
                className="w-full px-4 py-2.5 rounded-md bg-[var(--color-accent)] text-white text-sm font-medium hover:bg-[var(--color-accent-light)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? "Sending code..." : "Send code"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)] mb-2">
                  6-digit code
                </label>
                <div className="relative">
                  <LuKey className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-subtle)] pointer-events-none" />
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    pattern="\d{6}"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full pl-10 pr-3 py-2.5 rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] tracking-[0.4em] text-center font-mono text-lg placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:border-[var(--color-accent)] transition-colors"
                    placeholder="123456"
                    autoFocus
                  />
                </div>
              </div>
              {devHint && (
                <div className="flex gap-2 text-xs text-[var(--color-text-muted)] bg-[var(--color-bg)] border border-[var(--color-border)] rounded-md p-3">
                  <span className="text-[var(--color-warning)] shrink-0">·</span>
                  <span>{devHint}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={submitting || code.length !== 6}
                className="w-full px-4 py-2.5 rounded-md bg-[var(--color-accent)] text-white text-sm font-medium hover:bg-[var(--color-accent-light)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? "Verifying..." : "Sign in"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setDevHint(null);
                }}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
              >
                <LuArrowLeft className="w-3 h-3" />
                Use a different email
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-[11px] text-[var(--color-text-subtle)] mt-4">
          Operator allowlist controls who can sign in.
        </p>
      </div>
    </div>
  );
}
